import React, { Fragment, Component } from 'react'

import * as THREE from 'three'

import { Sprite3D } from '../sprite3D/'
import { HitPlane3D } from '../hitMesh3D'

import { dispose3DObject, standardizeScale } from '../../../utils'

class Button3D extends Component {
    constructor(props) {
        super(props)
        this.currentlyGrabbing
        this.offsetPosition
    }

    state = {
        visible: false,
        rootObj: null,
    }

    componentWillUnmount() {
        dispose3DObject(this.state.rootObj)
        this.rafMesh && cancelAnimationFrame(this.rafMesh)
        this.setState({ rootObj: null })
    }

    componentDidMount() {
        const { scene, meshPosition, rootObj } = this.props

        let _rootObj = new THREE.Object3D()
        _rootObj.name = 'buttonRoot'
        _rootObj.position.set(meshPosition.x, meshPosition.y, meshPosition.z)
        _rootObj.lookAt(new THREE.Vector3(0, 0, 0))
        this.setState({ rootObj: _rootObj })
        this.mountButton(_rootObj)
        if (rootObj) {
            rootObj.add(_rootObj)
        } else {
            scene.add(_rootObj)
        }
    }

    mountButton = rootObj => {
        const {
            createAnimations,
            camera,
            renderer,
            handleClick,
            hasBorder,
            hasScrubBar,
            handleMouseOver,
            handleMouseOut,
            isMobile,
        } = this.props

        this.buttonObj = this.addButton(rootObj)

        if (hasBorder) {
            this.addBorder(this.buttonObj)
        }

        if (hasScrubBar) {
            this.addScrubBar(this.buttonObj)
        }

        this.animateButton()

        // Rig up animations in HOC
        createAnimations && createAnimations(this.buttonObj)
    }

    addButton = rootObj => {
        const { width, height, edgeRadius, buttonColor, camera, renderer } = this.props

        // Rounded rectangle
        // Example: https://github.com/mrdoob/three.js/blob/master/examples/webgl_geometry_shapes.html
        // Creates dark foreground rectangle
        // Puts larger rectangle behind it to simulate bevel
        var shape = new THREE.Shape()
        ;(function roundedRect(ctx, x, y, width, height, radius) {
            ctx.moveTo(x, y + radius)
            ctx.lineTo(x, y + height - radius)
            ctx.quadraticCurveTo(x, y + height, x + radius, y + height)
            ctx.lineTo(x + width - radius, y + height)
            ctx.quadraticCurveTo(x + width, y + height, x + width, y + height - radius)
            ctx.lineTo(x + width, y + radius)
            ctx.quadraticCurveTo(x + width, y, x + width - radius, y)
            ctx.lineTo(x + radius, y)
            ctx.quadraticCurveTo(x, y, x, y + radius)
        })(shape, 0, 0, width, height, edgeRadius)

        var geometry = new THREE.ShapeBufferGeometry(shape)

        shape.autoClose = true

        var material = new THREE.MeshBasicMaterial({ color: buttonColor, side: THREE.DoubleSide })

        this.buttonObj = new THREE.Mesh(geometry, material)
        this.buttonObj.material.transparent = true
        this.buttonObj.material.opacity = 0.75

        this.buttonObj.position.set(-width / 2, -height / 2, 0.2)
        this.buttonObj.name = 'button'
        this.buttonObj.renderOrder = 5

        rootObj = standardizeScale(rootObj, camera, renderer, height)
        // Need to figure out a better way to manage thius
        // Short timeout prevents flash of unrotated components
        // setTimeout(() => {
        rootObj.add(this.buttonObj)
        // }, 50)

        return this.buttonObj
    }

    addBorder = parentObj => {
        const { width, height, edgeRadius, borderWidth, borderColor } = this.props
        var borderShape = new THREE.Shape()
        ;(function roundedRect(ctx, x, y, width, height, radius) {
            ctx.moveTo(x, y + radius)
            ctx.lineTo(x, y + height - radius)
            ctx.quadraticCurveTo(x, y + height, x + radius, y + height)
            ctx.lineTo(x + width - radius, y + height)
            ctx.quadraticCurveTo(x + width, y + height, x + width, y + height - radius)
            ctx.lineTo(x + width, y + radius)
            ctx.quadraticCurveTo(x + width, y, x + width - radius, y)
            ctx.lineTo(x + radius, y)
            ctx.quadraticCurveTo(x, y, x, y + radius)
        })(borderShape, 0, 0, width + borderWidth, height + borderWidth, edgeRadius)

        var borderGeo = new THREE.ShapeBufferGeometry(borderShape)
        var borderMaterial = new THREE.MeshBasicMaterial({ color: borderColor, side: THREE.DoubleSide })
        this.borderObj = new THREE.Mesh(borderGeo, borderMaterial)
        this.borderObj.material.transparent = true
        this.borderObj.material.opacity = 0.25
        this.borderObj.position.set(-0.025, -0.025, -0.01)

        this.borderObj.name = 'border'
        this.borderObj.renderOrder = 5
        parentObj.add(this.borderObj)
    }

    addScrubBar = parentObj => {
        const { scrubBarColor, height, width } = this.props
        var geometry = new THREE.PlaneBufferGeometry(width, height * 0.1, 32)

        var material = new THREE.MeshBasicMaterial({ color: scrubBarColor, side: THREE.DoubleSide })
        this.scrubBar = new THREE.Mesh(geometry, material)
        this.scrubBar.position.set(1, 0.025, 0.01)

        this.scrubBar.name = 'scrubBar'
        parentObj.add(this.scrubBar)
    }

    animateButton = () => {
        const { percentageComplete, width } = this.props
        // Rotate circle to face camera but don't rotate sprite
        // Keeps margin of circle constant wrt sprite image
        if (percentageComplete <= 1) {
            this.scrubBar && this.scrubBar.scale.set(percentageComplete, this.scrubBar.scale.y, this.scrubBar.scale.z)
            // Offset position to adjust for centered scaling
            // https://stackoverflow.com/questions/33454919/scaling-a-three-js-geometry-only-up
            this.scrubBar &&
                this.scrubBar.position.set(
                    (percentageComplete * width) / 2,
                    this.scrubBar.position.y,
                    this.scrubBar.position.z
                )
        }

        this.rafMesh = requestAnimationFrame(this.animateButton)
    }

    render() {
        const {
            scene,
            camera,
            renderer,
            spriteResource,
            width,
            height,
            showSprite,
            isMobile,
            handleClick,
            handleMouseOver,
            handleMouseOut,
        } = this.props
        const { rootObj } = this.state
        return (
            <Fragment>
                {rootObj ? (
                    <Fragment>
                        <Sprite3D
                            rootObj={rootObj}
                            scene={scene}
                            camera={camera}
                            renderer={renderer}
                            containerWidth={width}
                            containerHeight={height}
                            isVisible={showSprite}
                            spriteResource={spriteResource}
                            offsetCofactor={{
                                x: width / 2 - width * 0.75,
                                y: height / 4,
                                z: 0,
                            }}
                            scaleCofactor={{
                                // Mp unassigned or zero values to avoid three.js warnings
                                x: width ? width : 0.000001,
                                y: height ? height : 0.000001,
                                z: 1,
                            }}
                        />
                        <HitPlane3D
                            width={width}
                            height={height}
                            opacity={0}
                            rootObj={rootObj}
                            scene={scene}
                            camera={camera}
                            renderer={renderer}
                            handleClick={handleClick}
                            handleTouchStart={handleClick}
                            handleMouseOut={handleMouseOut}
                            handleMouseOver={handleMouseOver}
                            isMobile={isMobile}
                        />
                    </Fragment>
                ) : null}
            </Fragment>
        )
    }
}

Button3D.defaultProps = {
    meshPosition: {
        x: -20,
        y: 0,
        z: 0,
    },
    width: 2,
    height: 1,
    edgeRadius: 0.05,
    borderWidth: 0.05,
    scrubBarColor: 0xffffff,
    borderColor: 0xffffff,
    buttonColor: 0x000000,
    hasBorder: true,
    hasScrubBar: true,
}

export default Button3D
